import apiClient from '@/api/apiClient';
import type {
  PersonCertificateDocument,
  PersonCertificateSubmission,
  PersonCertificatesResponse,
  PersonCertificateType,
} from '@/types/person-certificates';
import type {
  CertificatesResponse,
  CompanyCertificateType,
  EntityDataRequestResponse,
  EntityDataRequestPayload,
  EntityDataProductKey,
  EntityRawDataset,
  LawsuitsResponse,
  MediaAiDataResponse,
  MediaAiDataParams,
  MediaAiFilterOptionField,
  MediaAiFilterOptionsResponse,
  OwnedCompaniesResponse,
  RawEntityType,
  RelationshipTreeAccessStatusResponse,
} from '@/types/entity-data';

interface LawsuitsParams {
  limit?: number;
  offset?: number;
  processNumber?: string;
  status?: string;
  includeOverview?: boolean;
}

const entityDataApi = {
  getPersonCertificates(entityId: string): Promise<PersonCertificatesResponse> {
    return apiClient.get(`/entity-data/person/${entityId}/certificates`);
  },

  requestPersonCertificates(entityId: string, certificateTypes: PersonCertificateType[]): Promise<PersonCertificateSubmission> {
    return apiClient.post(`/entity-data/person/${entityId}/certificates`, { certificateTypes });
  },

  getPersonCertificateDocument(entityId: string, certificateType: PersonCertificateType): Promise<PersonCertificateDocument> {
    return apiClient.get(`/entity-data/person/${entityId}/certificates/${certificateType}/document`);
  },
  getCompanyCertificateDocument(entityId: string, certificateType: CompanyCertificateType): Promise<{ url: string }> {
    return apiClient.get(`/entity-data/company/${entityId}/certificates/${certificateType}/document`);
  },
  getLawsuits(entityType: RawEntityType, entityId: string, params: LawsuitsParams = {}): Promise<LawsuitsResponse> {
    return apiClient.get(`/entity-data/${entityType}/${entityId}/lawsuits`, {
      params: {
        limit: params.limit ?? 20,
        offset: params.offset ?? 0,
        ...(params.processNumber ? { process_number: params.processNumber } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.includeOverview === false ? { include_overview: false } : {}),
      },
    });
  },

  getOwnedCompanies(entityType: RawEntityType, entityId: string): Promise<OwnedCompaniesResponse> {
    return apiClient.get(`/entity-data/${entityType}/${entityId}/owned-companies`);
  },

  getCertificates(entityType: RawEntityType, entityId: string): Promise<CertificatesResponse> {
    return apiClient.get(`/entity-data/${entityType}/${entityId}/certificates`);
  },

  getMediaAiData(entityType: RawEntityType, entityId: string, params: MediaAiDataParams = {}): Promise<MediaAiDataResponse> {
    return apiClient.get(`/entity-data/${entityType}/${entityId}/media-ai`, {
      params: {
        ...(params.categoryCode?.length ? { category_code: params.categoryCode } : {}),
        ...(params.riskLevel?.length ? { risk_level: params.riskLevel } : {}),
        ...(params.confidence?.length ? { confidence: params.confidence } : {}),
        ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
        ...(params.dateTo ? { date_to: params.dateTo } : {}),
        ...(params.sourceName?.length ? { source_name: params.sourceName } : {}),
        ...(params.articleId?.length ? { article_id: params.articleId } : {}),
        ...(params.citedEntityName?.length ? { cited_entity_name: params.citedEntityName } : {}),
      },
    });
  },

  getMediaAiFilterOptions(
    entityType: RawEntityType,
    entityId: string,
    field: MediaAiFilterOptionField,
    query = '',
    limit = 8,
  ): Promise<MediaAiFilterOptionsResponse> {
    return apiClient.get(`/entity-data/${entityType}/${entityId}/media-ai/filter-options`, {
      params: {
        field,
        ...(query.trim() ? { q: query.trim() } : {}),
        limit,
      },
    });
  },

  getDatasetAccess(
    entityType: RawEntityType,
    entityId: string,
    dataset: EntityRawDataset,
  ): Promise<RelationshipTreeAccessStatusResponse> {
    return apiClient.get(`/entity-data/${entityType}/${entityId}/has-access`, {
      params: {
        dataset,
      },
    });
  },

  getProductAccess(
    entityType: RawEntityType,
    entityId: string,
    productKey: EntityDataProductKey,
  ): Promise<RelationshipTreeAccessStatusResponse> {
    return apiClient.get(`/entity-data/${entityType}/${entityId}/has-access`, {
      params: {
        product_key: productKey,
      },
    });
  },

  requestDatasetAccess(
    entityType: RawEntityType,
    entityId: string,
    dataset: EntityRawDataset,
  ): Promise<EntityDataRequestResponse> {
    return apiClient.post(
      `/entity-data/${entityType}/${entityId}/request`,
      {},
      {
        params: {
          dataset,
        },
      },
    );
  },

  requestProductAccess(
    entityType: RawEntityType,
    entityId: string,
    productKey: EntityDataProductKey,
    payload: EntityDataRequestPayload = {},
  ): Promise<EntityDataRequestResponse> {
    return apiClient.post(
      `/entity-data/${entityType}/${entityId}/request`,
      payload,
      {
        params: {
          product_key: productKey,
        },
      },
    );
  },

  requestRelationshipTreeAccess(
    entityType: RawEntityType,
    entityId: string,
  ): Promise<EntityDataRequestResponse> {
    return entityDataApi.requestDatasetAccess(entityType, entityId, 'business_relationships');
  },
};

export default entityDataApi;
